import { initBrand } from "./brand";
// Suite-wide appearance — one look (mode, accent palette, top-bar style) for
// every TribeX app, with an optional per-app override ("Different look in
// Docs"). Resolution: the app's own look → the suite look → the defaults.
//
// Where it lives:
//  • Cookie `fg_look` on the parent domain (.fowlguard.com; host-only on
//    localhost, where ports share cookies anyway). Every app's index.html reads
//    it before first paint — keep BOOT_SNIPPET_NOTE below in step with the
//    inline scripts — and it's how a change made in one app reaches the others:
//    they re-read it on focus and every few seconds while visible.
//  • Account copy: Drive BFF `GET/PUT /api/me/appearance` (AppearanceEndpoints.cs),
//    so it follows the user to other devices. Last write wins by `updatedAt`.
//
// Cookie value (short, no characters that need escaping):
//   v1|<mode>.<palette>.<appbar>|<app>:<mode>.<palette>.<appbar>,…|<updatedAt ms>

import { useEffect, useRef, useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type PaletteId = "office" | "fluent" | "violet" | "teal" | "emerald" | "graphite" | "saffron" | "berry";
export type AppbarStyle = "accent" | "light" | "dark";
export type SuiteAppKey = "mail" | "drive" | "docs" | "sheets" | "slides" | "pdf" | "connect";

export interface Look {
    mode: ThemeMode;
    palette: PaletteId;
    appbar: AppbarStyle;
}

export interface SuiteAppearance {
    suite: Look;
    /** Apps with their own look; everything else follows `suite`. */
    own: Partial<Record<SuiteAppKey, Look>>;
    /** When it last changed (ms since epoch); 0 = never set. */
    updatedAt: number;
}

export const THEME_MODES: readonly ThemeMode[] = ["light", "dark", "system"];
export const APPBAR_STYLES: readonly AppbarStyle[] = ["accent", "light", "dark"];
export const SUITE_APP_KEYS: readonly SuiteAppKey[] = ["mail", "drive", "docs", "sheets", "slides", "pdf", "connect"];

export interface Palette {
    id: PaletteId;
    name: string;
    /** Accent in the light theme (white text on it). */
    light: string;
    /** Accent in the dark theme (dark text on it). */
    dark: string;
}

/** The eight accent palettes. CSS tokens live in globals.css (html[data-palette]); hex is for swatches + theme-color. */
export const PALETTES: readonly Palette[] = [
    { id: "office", name: "Classic Blue", light: "#0078d4", dark: "#3a96dd" },
    { id: "fluent", name: "Sky Blue", light: "#0f6cbd", dark: "#479ef5" },
    { id: "violet", name: "Violet", light: "#5b5fc7", dark: "#9ea2ff" },
    { id: "teal", name: "Teal", light: "#03787c", dark: "#3fc1c6" },
    { id: "emerald", name: "Emerald", light: "#107c41", dark: "#54c286" },
    { id: "graphite", name: "Graphite", light: "#3b4a5c", dark: "#9fb3c8" },
    { id: "saffron", name: "Saffron", light: "#b45309", dark: "#f5a524" },
    { id: "berry", name: "Berry", light: "#b4236b", dark: "#f06ba8" }
];

export function paletteById(id: PaletteId): Palette {
    return PALETTES.find((p) => p.id === id) ?? (PALETTES[0] as Palette);
}

export const DEFAULT_LOOK: Look = { mode: "system", palette: "office", appbar: "accent" };

const PALETTE_IDS = PALETTES.map((p) => p.id);

function oneOf<T extends string>(v: unknown, list: readonly T[]): T | undefined {
    return typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : undefined;
}

/** A valid Look from anything (object or "mode.palette.appbar"), or null. */
export function cleanLook(raw: unknown): Look | null {
    let src: Record<string, unknown> | null = null;
    if (typeof raw === "string") {
        const [mode, palette, appbar] = raw.split(".");
        src = { mode, palette, appbar };
    } else if (raw && typeof raw === "object") {
        src = raw as Record<string, unknown>;
    }
    if (!src) return null;
    const mode = oneOf(src.mode, THEME_MODES);
    const palette = oneOf(src.palette, PALETTE_IDS);
    const appbar = oneOf(src.appbar, APPBAR_STYLES);
    if (!mode && !palette && !appbar) return null;
    return { mode: mode ?? DEFAULT_LOOK.mode, palette: palette ?? DEFAULT_LOOK.palette, appbar: appbar ?? DEFAULT_LOOK.appbar };
}

export function defaultAppearance(): SuiteAppearance {
    return { suite: { ...DEFAULT_LOOK }, own: {}, updatedAt: 0 };
}

/** Keeps only valid fields (mirror of the Drive BFF's SuiteAppearanceSchema). */
export function sanitizeAppearance(raw: unknown): SuiteAppearance {
    const out = defaultAppearance();
    if (!raw || typeof raw !== "object") return out;
    const r = raw as Record<string, unknown>;
    out.suite = cleanLook(r.suite) ?? out.suite;
    if (r.own && typeof r.own === "object") {
        for (const key of SUITE_APP_KEYS) {
            const look = cleanLook((r.own as Record<string, unknown>)[key]);
            if (look) out.own[key] = look;
        }
    }
    const at = r.updatedAt;
    out.updatedAt = typeof at === "number" && Number.isFinite(at) && at > 0 ? Math.floor(at) : 0;
    return out;
}

const lookCode = (l: Look) => `${l.mode}.${l.palette}.${l.appbar}`;

export function encodeAppearance(a: SuiteAppearance): string {
    const own = SUITE_APP_KEYS.filter((k) => a.own[k]).map((k) => `${k}:${lookCode(a.own[k] as Look)}`).join(",");
    return `v1|${lookCode(a.suite)}|${own}|${a.updatedAt}`;
}

export function decodeAppearance(value: string): SuiteAppearance | null {
    const parts = value.split("|");
    if (parts[0] !== "v1") return null;
    const own: Record<string, string> = {};
    for (const pair of (parts[2] ?? "").split(",")) {
        const [k, v] = pair.split(":");
        if (k && v) own[k] = v;
    }
    return sanitizeAppearance({ suite: parts[1], own, updatedAt: Number(parts[3]) });
}

// ── Cookie ───────────────────────────────────────────────────────────────

const COOKIE = "fg_look";
/** The pre-sign-in theme key the old useSuiteTheme hook wrote (per origin). */
const LEGACY_THEME_KEY = "fg-suite-theme";

/**
 * BOOT_SNIPPET_NOTE — each app's index.html carries an inline copy of this logic
 * (read the cookie, pick the app's own look or the suite look, set `.dark`,
 * data-palette, data-appbar) so the first paint is already right.
 */
function cookieDomain(): string {
    const host = window.location.hostname;
    if (host === "localhost" || !host.includes(".") || /^[\d.]+$/.test(host) || host.includes(":")) return "";
    return `; domain=.${host.split(".").slice(-2).join(".")}`;
}

function readCookie(): string | null {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(/(?:^|;\s*)fg_look=([^;]*)/);
    if (!m?.[1]) return null;
    try {
        return decodeURIComponent(m[1]);
    } catch {
        return null;
    }
}

function writeCookie(a: SuiteAppearance) {
    if (typeof document === "undefined") return;
    const secure = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${COOKIE}=${encodeURIComponent(encodeAppearance(a))}; path=/; max-age=31536000; samesite=lax${cookieDomain()}${secure}`;
}

function readInitial(): SuiteAppearance {
    const fromCookie = readCookie();
    const decoded = fromCookie ? decodeAppearance(fromCookie) : null;
    if (decoded) return decoded;
    const a = defaultAppearance();
    try {
        const legacy = window.localStorage.getItem(LEGACY_THEME_KEY);
        if (legacy === "light" || legacy === "dark") a.suite.mode = legacy; // updatedAt stays 0: the account copy wins
    } catch {
        /* storage blocked */
    }
    return a;
}

// ── Store ────────────────────────────────────────────────────────────────

interface Snapshot {
    appearance: SuiteAppearance;
    systemDark: boolean;
}

const hasWindow = typeof window !== "undefined";
const darkMedia = hasWindow && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

let snapshot: Snapshot = {
    appearance: hasWindow ? readInitial() : defaultAppearance(),
    systemDark: Boolean(darkMedia?.matches)
};
let currentApp: SuiteAppKey | null = null;
const listeners = new Set<() => void>();
let lastCookie = hasWindow ? readCookie() : null;

function emit(next: Partial<Snapshot>) {
    snapshot = { ...snapshot, ...next };
    if (currentApp) applyLookToDocument(effectiveLook(currentApp, snapshot.appearance));
    for (const l of listeners) l();
}

export function getAppearance(): SuiteAppearance {
    return snapshot.appearance;
}

export function effectiveLook(app: SuiteAppKey, a: SuiteAppearance = snapshot.appearance): Look {
    return a.own[app] ?? a.suite;
}

export function resolveMode(mode: ThemeMode, systemDark = snapshot.systemDark): "light" | "dark" {
    return mode === "dark" || (mode === "system" && systemDark) ? "dark" : "light";
}

/** `.dark`, data-palette and data-appbar on <html>, plus the browser's theme-color. */
export function applyLookToDocument(look: Look) {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const dark = resolveMode(look.mode) === "dark";
    root.classList.toggle("dark", dark);
    if (look.palette === "office") root.removeAttribute("data-palette");
    else root.setAttribute("data-palette", look.palette);
    if (look.appbar === "accent") root.removeAttribute("data-appbar");
    else root.setAttribute("data-appbar", look.appbar);
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) {
        meta.content = look.appbar === "dark" || (dark && look.appbar === "accent")
            ? "#1c2026"
            : look.appbar === "light" ? (dark ? "#0a0f1c" : "#ffffff") : paletteById(look.palette).light;
    }
}

function commit(next: SuiteAppearance, push: boolean) {
    writeCookie(next);
    lastCookie = readCookie();
    emit({ appearance: next });
    if (push) schedulePush();
}

/** Change the look `app` shows: its own look when it has one, otherwise the suite look (every app). */
export function setLook(app: SuiteAppKey, patch: Partial<Look>) {
    const a = snapshot.appearance;
    const clean = cleanLook({ ...effectiveLook(app, a), ...patch });
    if (!clean) return;
    const own = a.own[app];
    const next: SuiteAppearance = own
        ? { ...a, own: { ...a.own, [app]: clean }, updatedAt: Date.now() }
        : { ...a, suite: clean, updatedAt: Date.now() };
    commit(next, true);
}

/** Give `app` its own look (starting from what it shows now), or send it back to the suite look. */
export function setOwnLook(app: SuiteAppKey, on: boolean) {
    const a = snapshot.appearance;
    const own = { ...a.own };
    if (on) own[app] = { ...effectiveLook(app, a) };
    else delete own[app];
    commit({ ...a, own, updatedAt: Date.now() }, true);
}

/** Another app (other origin, same cookie) changed it: show that here. */
function recheckCookie() {
    const now = readCookie();
    if (now === lastCookie) return;
    lastCookie = now;
    const decoded = now ? decodeAppearance(now) : null;
    if (decoded && decoded.updatedAt >= snapshot.appearance.updatedAt) emit({ appearance: decoded });
}

let initialised = false;

/**
 * Call once at start-up (main.tsx) with the app's key: applies its look now
 * and keeps it in step with the OS setting and with changes made in other apps.
 */
export function initSuiteAppearance(app: SuiteAppKey) {
    initBrand();
    currentApp = app;
    applyLookToDocument(effectiveLook(app));
    if (initialised || !hasWindow) return;
    initialised = true;
    darkMedia?.addEventListener?.("change", () => emit({ systemDark: Boolean(darkMedia.matches) }));
    window.addEventListener("focus", recheckCookie);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") recheckCookie();
    });
    window.setInterval(() => {
        if (document.visibilityState === "visible") recheckCookie();
    }, 4000);
}

export function subscribeAppearance(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** The appearance as `app` sees it, plus setters. Re-renders on any change. */
export function useSuiteAppearance(app?: SuiteAppKey) {
    const s = useSyncExternalStore(subscribeAppearance, () => snapshot, () => snapshot);
    const key = app ?? currentApp ?? "drive";
    const look = effectiveLook(key, s.appearance);
    return {
        appearance: s.appearance,
        look,
        /** "light" | "dark" actually showing ("system" resolved). */
        resolved: resolveMode(look.mode, s.systemDark),
        hasOwnLook: Boolean(s.appearance.own[key]),
        setLook: (patch: Partial<Look>) => setLook(key, patch),
        setOwnLook: (on: boolean) => setOwnLook(key, on)
    };
}

// ── Account copy (Drive BFF) ─────────────────────────────────────────────

interface SyncConfig {
    /** Drive BFF origin, e.g. https://drive.tribex.in (no trailing slash needed). */
    driveBaseUrl: string;
    getToken: () => string | undefined;
    /**
     * Used once when neither the account nor this browser has a saved look —
     * e.g. Mail's pre-suite appearance, so nobody loses the colours they picked.
     */
    seed?: () => Partial<Look> | null;
}

let sync: SyncConfig | null = null;
let pushTimer: ReturnType<typeof setTimeout> | undefined;
let pulledAt = 0;
let serverKnown = false;

const endpoint = () => `${(sync?.driveBaseUrl ?? "").replace(/\/+$/, "")}/api/me/appearance`;

function schedulePush(ms = 600) {
    if (!sync) return;
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(() => {
        pushTimer = undefined;
        void push();
    }, ms);
}

async function push() {
    const token = sync?.getToken();
    if (!sync || !token || !serverKnown) return;
    try {
        const res = await fetch(endpoint(), {
            method: "PUT",
            keepalive: true,
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(snapshot.appearance)
        });
        if (!res.ok) return;
        const body = (await res.json()) as { appearance?: unknown };
        const stored = sanitizeAppearance(body.appearance);
        if (stored.updatedAt > snapshot.appearance.updatedAt) commit(stored, false);
    } catch {
        /* offline — the cookie keeps it; pushed again on the next change or pull */
    }
}

async function pull() {
    const token = sync?.getToken();
    if (!sync || !token) return;
    try {
        const res = await fetch(endpoint(), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return; // e.g. 403 when the plan has no Drive: the cookie still works
        const body = (await res.json()) as { appearance?: unknown };
        serverKnown = true;
        pulledAt = Date.now();
        const server = body.appearance ? sanitizeAppearance(body.appearance) : null;
        const local = snapshot.appearance;
        if (server && server.updatedAt > local.updatedAt) {
            commit(server, false);
        } else if (local.updatedAt > (server?.updatedAt ?? 0)) {
            void push();
        } else if (!server && local.updatedAt === 0) {
            const seed = sync.seed?.();
            const look = seed ? cleanLook({ ...local.suite, ...seed }) : null;
            if (look) commit({ ...local, suite: look, updatedAt: Date.now() }, true);
        }
    } catch {
        /* offline */
    }
}

/**
 * Mount once in the signed-in shell: keeps the look in step with the account copy.
 * `token` must carry the Drive audience (fg.drive scope); without it the
 * cookie alone carries the look between apps on this device.
 */
export function useAppearanceSync(driveBaseUrl: string | undefined, token: string | undefined, seed?: () => Partial<Look> | null) {
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const seedRef = useRef(seed);
    seedRef.current = seed;
    const signedIn = Boolean(token);
    useEffect(() => {
        if (!driveBaseUrl || !signedIn) return;
        return startAppearanceSync({ driveBaseUrl, getToken: () => tokenRef.current, seed: () => seedRef.current?.() ?? null });
    }, [driveBaseUrl, signedIn]);
}

/** Start syncing with the account copy (useAppearanceSync wraps this). Returns a stop function. */
export function startAppearanceSync(config: SyncConfig): () => void {
    sync = config;
    void pull();
    const onVisible = () => {
        if (document.visibilityState === "visible" && Date.now() - pulledAt > 60_000) void pull();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
        document.removeEventListener("visibilitychange", onVisible);
        if (sync === config) sync = null;
    };
}
